import { mapListToRecords, normalizeList } from '@sophocles/entities/entityUtils';
import {
  MonthlyInternationalPowerMeterReport,
  monthlyInternationalPowerMeterReportSchema,
} from '@sophocles/entities/MonthlyInternationalPowerMeterReport/monthlyInternationalPowerMeterReportRecord';
import apiClient from '@sophocles/http-common';
import { checkHttpStatus, genericErrorAction } from '@sophocles/utils';
import { endOfDay, format, parseISO, startOfDay } from 'date-fns';
import { normalize } from 'normalizr';

/* Receive Actions */
export const RECEIVE_MONTHLY_INTERNATIONAL_POWER_METER_REPORTS = 'RECEIVE_MONTHLY_INTERNATIONAL_POWER_METER_REPORTS';
function receiveMonthlyInternationalPowerMeterReports(json) {
  return {
    type: RECEIVE_MONTHLY_INTERNATIONAL_POWER_METER_REPORTS,
    monthlyInternationalPowerMeterReports: mapListToRecords(json, MonthlyInternationalPowerMeterReport),
  };
}

/* Request Actions */
// starter request
export const REQUEST_MONTHLY_INTERNATIONAL_POWER_METER_REPORT = 'REQUEST_MONTHLY_INTERNATIONAL_POWER_METER_REPORT';
function requestMonthlyInternationalPowerMeterReport(monthlyInternationalPowerMeterReport_id) {
  return {
    type: REQUEST_MONTHLY_INTERNATIONAL_POWER_METER_REPORT,
    monthlyInternationalPowerMeterReport_id,
  };
}

export const REQUEST_MONTHLY_INTERNATIONAL_POWER_METER_REPORTS_FOR_SITE_WITH_RANGE =
  'REQUEST_MONTHLY_INTERNATIONAL_POWER_METER_REPORTS_FOR_SITE_WITH_RANGE';
function requestMonthlyInternationalPowerMeterReportsForSiteWithRange(site_id, from, to) {
  return {
    type: REQUEST_MONTHLY_INTERNATIONAL_POWER_METER_REPORTS_FOR_SITE_WITH_RANGE,
    site_id,
    from,
    to,
  };
}

/* Fetch Actions */
// starter fetch

export function fetchMonthlyInternationalPowerMeterReport(monthlyInternationalPowerMeterReport_id) {
  return dispatch => {
    dispatch(requestMonthlyInternationalPowerMeterReport(monthlyInternationalPowerMeterReport_id));
    return apiClient
      .get(`/monthlyinternationalpowermeterreports/${monthlyInternationalPowerMeterReport_id}/`)
      .then(checkHttpStatus)
      .then(response =>
        normalizeList(
          response.data,
          'monthlyInternationalPowerMeterReports',
          monthlyInternationalPowerMeterReportSchema,
        ),
      )
      .then(json =>
        dispatch(receiveMonthlyInternationalPowerMeterReports(json.entities.monthlyInternationalPowerMeterReports)),
      )
      .catch(error => dispatch(genericErrorAction(error, REQUEST_MONTHLY_INTERNATIONAL_POWER_METER_REPORT)));
  };
}

export function fetchMonthlyInternationalPowerMeterReportsForSiteWithRange(site_id, from, to) {
  const fromTS = startOfDay(parseISO(from));
  const toTS = endOfDay(parseISO(to));
  return dispatch => {
    dispatch(requestMonthlyInternationalPowerMeterReportsForSiteWithRange(site_id, fromTS, toTS));
    return apiClient
      .get('/monthlyinternationalpowermeterreports/', {
        params: {
          site: site_id,
          report_date__gte: format(fromTS, 'yyyy-MM-dd'),
          report_date__lte: format(toTS, 'yyyy-MM-dd'),
        },
      })
      .then(checkHttpStatus)
      .then(response =>
        normalizeList(
          response.data.results,
          'monthlyInternationalPowerMeterReports',
          monthlyInternationalPowerMeterReportSchema,
        ),
      )
      .then(json =>
        dispatch(receiveMonthlyInternationalPowerMeterReports(json.entities.monthlyInternationalPowerMeterReports)),
      )
      .catch(error =>
        dispatch(genericErrorAction(error, REQUEST_MONTHLY_INTERNATIONAL_POWER_METER_REPORTS_FOR_SITE_WITH_RANGE)),
      );
  };
}

/* Edit Actions */
export const EDIT_MONTHLY_INTERNATIONAL_POWER_METER_REPORT = 'EDIT_MONTHLY_INTERNATIONAL_POWER_METER_REPORT';
export function editMonthlyInternationalPowerMeterReport(monthlyInternationalPowerMeterReport_id) {
  return {
    type: EDIT_MONTHLY_INTERNATIONAL_POWER_METER_REPORT,
    monthlyInternationalPowerMeterReport: monthlyInternationalPowerMeterReport_id,
  };
}

export function patchMonthlyInternationalPowerMeterReport(monthlyInternationalPowerMeterReport_id, values) {
  return dispatch => {
    dispatch(editMonthlyInternationalPowerMeterReport(monthlyInternationalPowerMeterReport_id));
    return apiClient
      .patch(`/monthlyinternationalpowermeterreports/${monthlyInternationalPowerMeterReport_id}/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, monthlyInternationalPowerMeterReportSchema))
      .then(json =>
        dispatch(receiveMonthlyInternationalPowerMeterReports(json.entities.monthlyInternationalPowerMeterReports)),
      )
      .catch(error => dispatch(genericErrorAction(error, EDIT_MONTHLY_INTERNATIONAL_POWER_METER_REPORT)));
  };
}

/* Create Actions */
export const CREATE_MONTHLY_INTERNATIONAL_POWER_METER_REPORT = 'CREATE_MONTHLY_INTERNATIONAL_POWER_METER_REPORT';
function sayCreateMonthlyInternationalPowerMeterReport(values) {
  return {
    type: CREATE_MONTHLY_INTERNATIONAL_POWER_METER_REPORT,
    values: values,
  };
}

export function createMonthlyInternationalPowerMeterReport(values) {
  return dispatch => {
    dispatch(sayCreateMonthlyInternationalPowerMeterReport(values));
    return apiClient
      .post(`/monthlyinternationalpowermeterreports/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, monthlyInternationalPowerMeterReportSchema))
      .then(json =>
        dispatch(receiveMonthlyInternationalPowerMeterReports(json.entities.monthlyInternationalPowerMeterReports)),
      )
      .catch(error => dispatch(genericErrorAction(error, CREATE_MONTHLY_INTERNATIONAL_POWER_METER_REPORT)));
  };
}

/* Delete Actions */
export const DELETE_MONTHLY_INTERNATIONAL_POWER_METER_REPORT = 'DELETE_MONTHLY_INTERNATIONAL_POWER_METER_REPORT';
function sayDeleteMonthlyInternationalPowerMeterReport(monthlyInternationalPowerMeterReport_id) {
  return {
    type: DELETE_MONTHLY_INTERNATIONAL_POWER_METER_REPORT,
    monthlyInternationalPowerMeterReport: monthlyInternationalPowerMeterReport_id,
  };
}

export function deleteMonthlyInternationalPowerMeterReport(monthlyInternationalPowerMeterReport_id) {
  return dispatch =>
    apiClient
      .delete(`/monthlyinternationalpowermeterreports/${monthlyInternationalPowerMeterReport_id}/`)
      .then(checkHttpStatus)
      .then(r => dispatch(sayDeleteMonthlyInternationalPowerMeterReport(monthlyInternationalPowerMeterReport_id)))
      .catch(error => dispatch(genericErrorAction(error, DELETE_MONTHLY_INTERNATIONAL_POWER_METER_REPORT)));
}
